import { Mail, MoreHorizontal, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useRemoveMember, useUpdateMemberRole } from '@/api/organizations/queries';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { Member } from '@/lib/auth-client';
import { getRoleBadgeVariant, getUserInitials } from './utils';

interface MembersTableProps {
  members: Member[];
  activeMember: Member;
}

const canManageMembers = (activeMember: Member, member: Member) => {
  if (activeMember.role === 'member') {
    return false;
  }

  if (activeMember.role === 'admin' && member.role === 'owner') {
    return false;
  }

  if (activeMember.id === member.id) {
    return false;
  }

  return true;
};

export function MembersTable({ members, activeMember }: MembersTableProps) {
  const removeMemberMutation = useRemoveMember();
  const updateMemberRoleMutation = useUpdateMemberRole();

  const handleRemoveMember = async (memberUserId: string, memberEmail: string) => {
    try {
      await removeMemberMutation.mutateAsync({
        memberIdOrEmail: memberUserId,
        organizationId: '', // This should be populated from context
      });

      toast.success(`${memberEmail} has been removed from the organization`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to remove member');
    }
  };

  const handleUpdateRole = async (
    memberUserId: string,
    newRole: 'member' | 'admin' | 'owner',
    memberEmail: string,
  ) => {
    try {
      await updateMemberRoleMutation.mutateAsync({
        memberId: memberUserId,
        role: newRole,
      });

      toast.success(`${memberEmail} role updated to ${newRole}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update member role');
    }
  };

  const isOwner = activeMember.role === 'owner';

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Members <span className="text-muted-foreground">{members.length || 0}</span>
        </CardTitle>
        <CardDescription>All members of your organization and their current status</CardDescription>
      </CardHeader>
      <CardContent className="mx-[-16px] px-4">
        {members.length === 0 ? (
          <div className="text-center py-8">
            <Mail className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No members</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by inviting your first team member.
            </p>
          </div>
        ) : (
          <Table containerClassName="px-4">
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="w-[50px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.id}>
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={member.user.image || ''} />
                        <AvatarFallback>
                          {getUserInitials(member.user.name, member.user.email)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="font-medium">{member.user.name}</div>
                    </div>
                  </TableCell>
                  <TableCell>{member.user.email}</TableCell>
                  <TableCell>
                    <Badge variant={getRoleBadgeVariant(member.role)}>{member.role}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(member.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {canManageMembers(activeMember, member) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {isOwner && (
                            <>
                              <DropdownMenuItem
                                onClick={() =>
                                  handleUpdateRole(member.id, 'admin', member.user.email)
                                }
                                disabled={member.role === 'admin' || member.role === 'owner'}
                              >
                                Make Admin
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  handleUpdateRole(member.id, 'member', member.user.email)
                                }
                                disabled={member.role === 'member' || member.role === 'owner'}
                              >
                                Make Member
                              </DropdownMenuItem>
                            </>
                          )}
                          {(isOwner ||
                            (activeMember.role === 'admin' && member.role === 'member')) && (
                            <DropdownMenuItem
                              onClick={() => handleRemoveMember(member.id, member.user.email)}
                              className="text-destructive"
                              disabled={member.role === 'owner' || member.id === activeMember?.id}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Remove Member
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
